import { useEffect, useRef, useState } from 'react';
import useMfe from 'utils/mfe/useMfe';
import externals from "utils/externals";
import { MainLoader } from '@netapp/bxp-design-system-react';
import useCurrentService from 'utils/useCurrentService';
import MainErrorBoundary from '../MainErrorBoundary';
import { useDispatch } from 'react-redux';
import { useTranslation } from "react-i18next";
import { mfe as i18nNamespace } from "../../../../i18n/namespace";

const statusEnums = {
    LOADING: 'loading',
    LOADED: 'loaded',
    ERROR: 'error',
};

let scriptEl = null;

const loadRemoteScript = (url, scope, translation) => {
    return new Promise((resolve, reject) => {
        if (window[scope]) return resolve(window[scope]);
        const script = document.createElement("script");
        script.src = url;
        script.type = "module";
        script.async = true;
        script.onload = () => {
            if (window[scope]) resolve(window[scope]);
            else reject(new Error(translation('scope_not_found', {scope})));
        };
        script.onerror = reject;
        document.head.appendChild(script);
        scriptEl = Object.assign(script);
    });
};

const RenderShadowDOM = ({ serviceKey, setWizardOpen, scope }) => {
    const hostRef = useRef(null);
    const shadowRootRef = useRef(null);
    const unmountRef = useRef();
    const [status, setStatus] = useState(statusEnums.LOADING);
    const { initializationData } = useMfe({setWizardOpen, endpointId: serviceKey}); 
    const currentService = useCurrentService();
    const dispatch = useDispatch();
    const { t } = useTranslation(i18nNamespace);

    useEffect(() => {
        let mounted = true;

        const loadRemoteModule = async () => {
            try {
                if (hostRef.current && !shadowRootRef.current) {
                    shadowRootRef.current = hostRef.current.attachShadow({ mode: 'open' });
                    const remoteApp = await loadRemoteScript(externals.serviceShadowDoms[serviceKey], scope, t);
                    remoteApp.mount(shadowRootRef.current, initializationData);
                    unmountRef.current = remoteApp.unmount;
                    if (mounted) setStatus(statusEnums.LOADED);
                }
            } catch (err) {
                console.error(`Failed to load ${currentService?.name} service:`, err);
                dispatch({
                    type: 'NOTIFICATIONS:ERROR',
                    payload: {
                        message: t('failed_to_load', { currentService: currentService?.name }),
                        exception: err,
                        group: "SHADOW_DOM_ERROR",
                    }
                });
                if (mounted) setStatus(statusEnums.ERROR);
            }
        };

        loadRemoteModule();

        return () => {
            if (unmountRef.current) {
                unmountRef.current();
                mounted = false;
                if (scriptEl && scriptEl.parentNode) {
                    scriptEl.parentNode.removeChild(scriptEl);
                }
            }
        };
    }, []);

    const ErrorComponent = () => {
      throw new Error("Failed to load the Shadow DOM application.");
    };

    return (
        <MainErrorBoundary>
            <div id="platform-shadow-host" style={{height:"100%"}} ref={hostRef}>
                {status === statusEnums.LOADING && <MainLoader/>}
                {status === statusEnums.ERROR && ErrorComponent()}
            </div>
        </MainErrorBoundary>
    );
}
export default RenderShadowDOM;